//@ts-nocheck
import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { routingMixin } from "@jack-henry/web-component-router";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/icons/jha-icon-warning";
import "@banno/jha-wc/src/icons/jha-icon-checkmark";
import "@banno/jha-wc/src/icons/jha-icon-save";
import "@banno/jha-wc/src/icons/jha-icon-circle-checkmark-filled";
import "@banno/jha-wc/src/icons/jha-icon-warning-filled";
import "@banno/jha-wc/src/jha-tab/jha-tab";
import "@banno/jha-wc/src/jha-tab-item/jha-tab-item";
import "../../Components/Loader";
import { TemplateStringInput } from "../../Components/TemplateStringInput";
import { DEFAULT_WORKFLOW_TYPE_ODI } from "../../types/Workflow/WorkflowDataType";
import Cookies from "js-cookie";
import {
  decryptValue,
  isBusinessUnitAdminUserType,
  isEnterpriseAdminUserType,
  showToastMessage,
  stringComparison,
} from "../../utils/app-utils";
import { businessUnitAdminCanMutateTemplate } from "../../utils/workflowTemplateAccess";
import { AppRouters } from "../../utils/app-router";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import { GlobalVariables } from "../../utils/constants";
import { Apis } from "../../utils/api-constants";
import { getRequest, postRequest, putRequest, fixedEncodeURIComponent } from "../../utils/jha-fetch";
import {
  WorkflowTemplateGeneralViewMode,
  WorkflowTemplateTabValidationState,
  TemplateMode,
  TabName,
  TransferType,
} from "../../types/WorkflowTemplates/WorkflowTemplateTypeIndex";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { WorkflowTemplateValidationStateManager } from "../../services/WorkflowTemplateValidationStateManager";
import {
  collectStepTemplateFieldStrings,
  ensureVariablesIncludeBuiltinRegionForApi,
  extractTemplateVariableNames,
  findDeprecatedLegacyBracketTemplateNames,
  findWorkflowTemplateStepCredentialLiterals,
  hasDeprecatedLegacyBracketTemplateSyntax,
  isBuiltinBusUnitVariableName,
  isBuiltinRegionVariableName,
} from "../../utils/workflowTemplatePlaceholderSyntax";

/**
 * Workflow Template General View
 *
 * This component serves as the shell/container for creating, editing, or viewing workflow templates.
 * It manages the tab navigation and overall save/cancel actions.
 *
 * Modes:
 * - ADD: Creating a new template
 * - EDIT: Modifying an existing template
 * - VIEW: Read-only view of a template (for non-admin or active templates)
 *
 * Architecture:
 * - Each tab is its own custom element for modularity
 * - Parent component tracks validation state of all tabs
 * - Save button enabled only when all tabs are valid
 * - Uses tab icons to show validation status (warning/checkmark)
 */
@customElement("workflow-template-general-view")
export class WorkflowTemplateGeneralView extends (routingMixin(LitElement) as unknown as typeof LitElement) {
  static get is() {
    return "workflow-template-general-view";
  }

  constructor() {
    super();
    // Initialize validation state manager - will be reset with correct mode in connectedCallback
    this.validationManager = new WorkflowTemplateValidationStateManager("ADD");

    // Subscribe to state changes to trigger re-renders
    this.validationManager.subscribe(() => {
      this.requestUpdate();
    });
  }

  // Mode Detection
  @property({ type: String }) mode: WorkflowTemplateGeneralViewMode = "ADD";
  @property({ type: String, reflect: true, attribute: "workflow-template-id" })
  workflowTemplateId: string = "";
  @property({ type: String }) templateStatus: string = "";
  @state() templateName: string = ""; // Store actual template name for updates
  @state() templateETag: string = ""; // Store ETag for optimistic locking on updates
  @state() templateData: any = null; // Full template data fetched once, passed to child tabs

  // User Permissions (refreshed from cookies in connectedCallback)
  @property({ type: Boolean }) isReadOnlyUser = false;
  @property({ type: Boolean }) isEnterpriseAdmin = false;
  @property({ type: Boolean }) isBusinessUnitAdmin = false;
  @property({ type: String }) loggedUserBu = "";

  private refreshRoleFromCookies(): void {
    const userType = decryptValue(Cookies.get("userType") || "");
    const userRole = decryptValue(Cookies.get("userRole") || "");
    this.isEnterpriseAdmin = isEnterpriseAdminUserType(userType);
    this.isBusinessUnitAdmin = isBusinessUnitAdminUserType(userType);
    this.isReadOnlyUser = /readonly/i.test(userRole);
    this.loggedUserBu = (Cookies.get("businessUnit") || "").trim().toLowerCase();
  }

  // Tab Management
  @property({ type: String }) activeTab = "settings";

  // Validation State for Each Tab (includes both validity and dirty state)
  @state() tabValidation: WorkflowTemplateTabValidationState = {
    settingsValid: false,
    workflowInformationValid: false,
    notificationValid: true, // Optional
    stepsValid: true, // Optional - templates can have no steps
    schedulerValid: true, // Optional
    allValid: false,
    // Dirty state tracking - true means unsaved changes exist
    settingsDirty: false,
    workflowInformationDirty: false,
    notificationDirty: false,
    stepsDirty: false,
    schedulerDirty: false,
    anyDirty: false,
  };

  // Variable validation errors per tab - tracks which tabs have undefined variables
  // This is separate from tabValidation to avoid interfering with other validation logic
  @state() private variableErrors: {
    settings: boolean;
    workflowInfo: boolean;
    notification: boolean;
    steps: boolean;
    scheduler: boolean;
  } = {
      settings: false,
      workflowInfo: false,
      notification: false,
      steps: false,
      scheduler: false,
    };

  // Variables currently in use in template fields - prevents deletion
  @state() private variablesInUse: string[] = [];

  /** True when any template field still uses deprecated [variableName] legacy syntax (not file-rename tokens). */
  @state() private showLegacyBracketWarning = false;

  // Centralized Validation State Manager - single source of truth for all validation
  private validationManager: WorkflowTemplateValidationStateManager;

  // Bound event handler for cleanup
  private boundHandleTemplateInputBlur: ((e: Event) => void) | null = null;

  // Bound event handler for beforeunload (browser navigation warning)
  private boundHandleBeforeUnload: ((e: BeforeUnloadEvent) => void) | null = null;

  /**
   * Check if any tab has undefined variable errors
   */
  private hasAnyVariableErrors(): boolean {
    return (
      this.variableErrors.settings ||
      this.variableErrors.workflowInfo ||
      this.variableErrors.notification ||
      this.variableErrors.steps ||
      this.variableErrors.scheduler
    );
  }

  /** When false, the user cannot persist template changes (view route or read-only role) — skip unsaved prompts. */
  private get canEditWorkflowTemplate(): boolean {
    return this.mode !== "VIEW" && !this.isReadOnlyUser;
  }

  // Loading States
  @property({ type: Boolean }) apiLoading = false;
  @property({ type: Boolean }) saveInProgress = false;

  // Page Title
  @property({ type: String }) pageTitle = "Add Workflow Template";

  // Cross-tab data - TransferType affects Steps and Scheduler behavior
  @state() transferType: string = "";

  static readonly styles = css`
    #template-container {
      padding: 24px;
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    #tabsWrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    #tabContent {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow-y: scroll;
    }

    /* Tab panel visibility - keep all tabs in DOM but hide inactive ones */
    .tab-panel {
      display: none;
      flex: 1;
      flex-direction: column;
    }

    .tab-panel[active] {
      display: flex;
    }

    #actionBtns {
      display: flex;
      gap: 12px;
      padding-top: 16px;
      justify-content: flex-start;
    }

    jha-tab-item > [icon] {
      margin-left: 8px;
      vertical-align: middle;
    }

    jha-icon-circle-checkmark-filled {
      fill: var(--jha-color-success);
    }

    jha-icon-warning-filled {
      fill: var(--jha-color-warning);
    }

    jha-icon-save {
      fill: var(--jha-color-info, #2196f3);
    }

    @media (prefers-color-scheme: dark) {
      h1,
      h2 {
        color: #a3d4ff;
      }
    }

    @media (prefers-color-scheme: light) {
      h1,
      h2 {
        color: #06185f;
      }
    }

    /* Light warning surface: always use dark text (do not inherit; dark theme sets parent text to near-white). */
    .legacy-bracket-banner {
      margin: 12px 0 0;
      padding: 10px 14px;
      border-radius: 6px;
      font-size: 13px;
      line-height: 1.45;
      background: var(--jha-color-warning-tint, #fff8e6);
      border: 1px solid var(--jha-color-warning, #e9a100);
      color: #1a1a1a;
    }

    .legacy-bracket-banner strong {
      color: #111827;
      font-weight: 600;
    }
  `;

  async connectedCallback() {
    // Set loading state and determine initial mode BEFORE calling super.connectedCallback()
    // This prevents the UI from rendering with wrong values
    this.refreshRoleFromCookies();

    // Check if we have a template ID (Edit or View mode)
    if (this.workflowTemplateId) {
      this.apiLoading = true; // Show loading state

      // Determine initial mode based on URL (synchronously, before fetching data)
      const currentPath = window.location.pathname;
      const isViewRoute = currentPath.includes("/view-workflow-template/");

      // Set initial mode based on route and user permissions
      if (isViewRoute || this.isReadOnlyUser) {
        this.mode = "VIEW";
        this.validationManager.setMode("VIEW");
        this.pageTitle = "View Workflow Template";
      } else {
        // Tentatively set to EDIT (may change to VIEW if template is ACTIVE)
        this.mode = "EDIT";
        this.validationManager.setMode("EDIT");
        this.pageTitle = "Edit Workflow Template";
      }

    } else {
      // No template ID means we're adding a new template (EA R/W and BUA R/W only; read-only roles cannot create)
      if (this.isReadOnlyUser) {
        this.mode = "VIEW";
        this.validationManager.setMode("VIEW");
        this.pageTitle = "Workflow Templates";
        document.title = GlobalVariables.MAIN_TITLE?.concat(" >", this.pageTitle);
        super.connectedCallback();
        AppRouters.go("/workflow-templates");
        return;
      }
      this.mode = "ADD";
      this.validationManager.setMode("ADD");
      this.pageTitle = "Add Workflow Template";
    }

    // Set page title early
    document.title = GlobalVariables.MAIN_TITLE?.concat(" >", this.pageTitle);

    // NOW call super to render with the correct initial mode
    super.connectedCallback();

    // Set up live variable validation - listen for custom blur event from template-string-input
    // This event is dispatched with bubbles: true and composed: true, so it crosses Shadow DOM
    this.boundHandleTemplateInputBlur = this.handleTemplateInputBlur.bind(this);
    document.addEventListener("template-input-blur", this.boundHandleTemplateInputBlur as EventListener);

    // Set up beforeunload handler to warn about unsaved changes when navigating away
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this);
    window.addEventListener("beforeunload", this.boundHandleBeforeUnload);

    // If we have a template ID, fetch the data to verify mode
    if (this.workflowTemplateId) {
      await this.loadTemplateData();

      // Update mode if template is ACTIVE (only matters for edit route)
      const isTemplateActive = stringComparison(this.templateStatus, "ACTIVE");
      if (isTemplateActive && this.mode === "EDIT") {
        // Template is active, force VIEW mode
        this.mode = "VIEW";
        this.validationManager.setMode("VIEW");
        this.pageTitle = "View Workflow Template";
        document.title = GlobalVariables.MAIN_TITLE?.concat(" >", this.pageTitle);
      }

      // BU Admin: cannot edit global templates, other-BU templates, or legacy rows missing valid scope (deep-link / bookmark protection)
      if (this.isBusinessUnitAdmin && this.mode === "EDIT") {
        if (!businessUnitAdminCanMutateTemplate(this.templateData ?? {}, this.loggedUserBu)) {
          this.mode = "VIEW";
          this.validationManager.setMode("VIEW");
          this.pageTitle = "View Workflow Template";
          document.title = GlobalVariables.MAIN_TITLE?.concat(" >", this.pageTitle);
        }
      }

      this.apiLoading = false;

      // After loading, update the list of variables in use (for deletion prevention)
      // Use requestAnimationFrame to ensure child tabs have rendered
      requestAnimationFrame(() => {
        this.updateVariablesInUse();
      });
    }
  }

  disconnectedCallback() {
    // Clean up event listener from document
    if (this.boundHandleTemplateInputBlur) {
      document.removeEventListener("template-input-blur", this.boundHandleTemplateInputBlur as EventListener);
      this.boundHandleTemplateInputBlur = null;
    }
    // Clean up beforeunload handler
    if (this.boundHandleBeforeUnload) {
      window.removeEventListener("beforeunload", this.boundHandleBeforeUnload);
      this.boundHandleBeforeUnload = null;
    }
    // Clean up state manager subscription
    this.validationManager.unsubscribe();
    super.disconnectedCallback();
  }

  /**
   * Handle browser navigation (back button, closing tab, refresh) when there are unsaved changes.
   * Shows a browser-native confirmation dialog.
   */
  private handleBeforeUnload(e: BeforeUnloadEvent): void {
    if (this.tabValidation.anyDirty && this.canEditWorkflowTemplate) {
      e.preventDefault();
      e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
    }
  }

  /**
   * Handle custom blur events from template-string-input elements.
   * Validates variable usage when the user leaves a field.
   * Listens for the custom 'template-input-blur' event which fires reliably on tab/click away.
   */
  private handleTemplateInputBlur(e: Event): void {
    // Use composedPath to find the template-string-input in the event path
    // This is necessary because custom events bubble through Shadow DOM
    const path = e.composedPath();
    const templateInput = path.find(
      (el) => (el as Element)?.tagName?.toLowerCase() === "template-string-input"
    ) as TemplateStringInput | undefined;

    if (!templateInput) {
      return; // Not from a template-string-input, ignore
    }

    // Get defined variables from settings tab
    const settingsTab = this.getSettingsTab();
    if (!settingsTab) return;

    const settingsData = settingsTab.getFormData();
    const definedVariables = settingsData?.variables || [];

    // Get the list of defined variable names (case-insensitive comparison)
    const definedVarNames = new Set(
      (definedVariables || []).map((v: any) => (v.name || "").toLowerCase())
    );

    // Check this input for undefined variables
    const value = templateInput.value || "";
    const placeholders = this.extractVariablePlaceholders(value);
    const undefinedVars: string[] = [];

    for (const placeholder of placeholders) {
      if (!definedVarNames.has(placeholder.toLowerCase())) {
        if (!undefinedVars.includes(placeholder)) {
          undefinedVars.push(placeholder);
        }
      }
    }

    // Update this input's error state
    if (undefinedVars.length > 0) {
      const varList = undefinedVars.map((v) => `{${v}}`).join(", ");
      const plural = undefinedVars.length > 1 ? "s" : "";
      templateInput.errorText = `Variable${plural} ${varList} ${undefinedVars.length > 1 ? "have" : "has"} not been defined`;
      templateInput.forceShowWarning = true;
    } else {
      // Clear error if it was a variable validation error
      if (templateInput.errorText?.includes("not been defined")) {
        templateInput.errorText = "";
        templateInput.forceShowWarning = false;
      }
    }

    // Update tab icon for this input's tab
    const tabName = this.getTabNameForElement(templateInput);
    this.updateVariableErrorsForTab(tabName, definedVarNames);

    // Update the list of variables in use for deletion prevention
    this.updateVariablesInUse();
  }

  /**
   * Map tab display names to internal tab keys
   */
  private getTabKeyFromName(tabName: string): keyof typeof this.variableErrors | null {
    switch (tabName) {
      case "Template Settings": return "settings";
      case "Workflow Information": return "workflowInfo";
      case "Notifications": return "notification";
      case "Workflow Steps": return "steps";
      case "Scheduler": return "scheduler";
      default: return null;
    }
  }

  /**
   * Update variable error state for a specific tab.
   * Re-checks all template-string-input elements in the tab.
   */
  private updateVariableErrorsForTab(tabName: string, definedVarNames: Set<string>): void {
    const tabKey = this.getTabKeyFromName(tabName);
    if (!tabKey) return;

    // Find all template-string-input elements in this component's shadow DOM tree
    const templateInputs = this.shadowRoot
      ? this.findAllTemplateStringInputs(this.shadowRoot)
      : [];

    // Check if this tab has any inputs with undefined variables
    let tabHasErrors = false;

    for (const input of templateInputs) {
      const inputTabName = this.getTabNameForElement(input);
      if (inputTabName !== tabName) continue;

      const value = input.value || "";
      const placeholders = this.extractVariablePlaceholders(value);

      for (const placeholder of placeholders) {
        if (!definedVarNames.has(placeholder.toLowerCase())) {
          tabHasErrors = true;
          break;
        }
      }

      if (tabHasErrors) break;
    }

    // Update the tab error state only if it changed
    if (this.variableErrors[tabKey] !== tabHasErrors) {
      this.variableErrors = {
        ...this.variableErrors,
        [tabKey]: tabHasErrors,
      };
      // Recalculate allValid since variable errors affect Save button state
      this.recalculateAllValid();
    }
  }

  /**
   * Recalculate allValid state (called after variable errors change)
   */
  private recalculateAllValid(): void {
    const isPullType = stringComparison(this.transferType, "Pull");
    const schedulerRequired = isPullType ? this.tabValidation.schedulerValid : true;
    const hasVariableErrors = this.hasAnyVariableErrors();

    this.tabValidation.allValid =
      this.tabValidation.settingsValid &&
      this.tabValidation.workflowInformationValid &&
      this.tabValidation.notificationValid &&
      this.tabValidation.stepsValid &&
      schedulerRequired &&
      !hasVariableErrors;

    this.requestUpdate();
  }

  /**
   * Load template data for EDIT/VIEW mode
   */
  async loadTemplateData() {
    this.apiLoading = true;

    try {
      // GET returns entity directly (PascalCase) - Workflow pattern
      const templateData = await getRequest(Apis.GET_WORKFLOW_TEMPLATE + `/${this.workflowTemplateId}`);

      if (templateData && !templateData?.details?.length) {
        // Store the template status to determine mode
        this.templateStatus = templateData.status || "";

        // Store the template name for updates (DynamoDB uses templateName as key)
        this.templateName = templateData.templateName || this.workflowTemplateId;

        // Store ETag for optimistic locking on updates
        this.templateETag = templateData.eTag || "";

        // Store full template data to pass to child tabs (single fetch, no duplicate API calls)
        this.templateData = templateData;

        // Update variables in use immediately when data is loaded
        this.updateVariablesInUse();
      } else {
        const errMsg = templateData?.details?.length > 0
          ? getErrorMessage(templateData.details)
          : "Failed to load template data.";
        showToastMessage(errMsg, "danger");
        this.onCancelClick(); // Navigate back to list
      }
    } catch (error) {
      showToastMessage("An error occurred while loading the template.", "danger");
      this.onCancelClick(); // Navigate back to list
    } finally {
      this.apiLoading = false;
    }
  }

  /**
   * Handle tab click event
   */
  onTabClick(tabId: string) {
    this.activeTab = tabId;

    // When switching to Settings tab, refresh variables in use
    // This ensures delete protection is up-to-date with any changes made in other tabs
    if (tabId === "settings") {
      this.updateVariablesInUse();
    }
  }

  /**
   * Handle validation updates from child tab components
   * Event detail includes: { tabName, valid, dirty, midEdit?, transferType?, variables? }
   * - valid: whether all required fields are filled
   * - dirty: whether there are unsaved changes (optional, defaults to current state)
   * - midEdit: whether a row/wizard is being edited (blocks save)
   * - transferType: from workflowInfo tab, affects scheduler requirements
   * - variables: from settings tab, list of defined variables
   * 
   * Routes events through the centralized ValidationStateManager while maintaining
   * backward compatibility with the existing tabValidation state object.
   */
  onTabValidationChange(e: CustomEvent) {
    const { tabName, valid, dirty, midEdit, transferType, variables } = e.detail;

    // Map legacy tab names to state manager tab names
    const tabNameMap: Record<string, TabName> = {
      settings: "settings",
      workflowInfo: "workflowInfo",
      notification: "notification",
      steps: "steps",
      scheduler: "scheduler",
    };

    const managerTabName = tabNameMap[tabName];
    if (managerTabName) {
      // Route through state manager
      this.validationManager.handleValidationChange({
        tabName: managerTabName,
        valid,
        dirty,
        midEdit,
        transferType: transferType as TransferType,
        variables,
      });
    }

    // Also update legacy state for backward compatibility during transition
    switch (tabName) {
      case "settings":
        this.tabValidation.settingsValid = valid;
        if (dirty !== undefined) this.tabValidation.settingsDirty = dirty;
        // Sync variables to other tabs whenever settings tab updates
        this.syncVariablesToTabs();
        // Re-validate all fields to clear/update errors based on current variables
        this.revalidateAllVariableFields();
        break;
      case "workflowInfo":
        this.tabValidation.workflowInformationValid = valid;
        if (dirty !== undefined) this.tabValidation.workflowInformationDirty = dirty;
        // Update transferType when it changes from Info tab
        if (transferType !== undefined) {
          this.transferType = transferType;
        }
        break;
      case "notification":
        this.tabValidation.notificationValid = valid;
        if (dirty !== undefined) this.tabValidation.notificationDirty = dirty;
        break;
      case "steps":
        this.tabValidation.stepsValid = valid;
        if (dirty !== undefined) this.tabValidation.stepsDirty = dirty;
        break;
      case "scheduler":
        this.tabValidation.schedulerValid = valid;
        if (dirty !== undefined) this.tabValidation.schedulerDirty = dirty;
        break;
    }

    // Calculate overall validity
    // During transition, use legacy state calculation to maintain backward compatibility
    // The state manager tracks midEdit state separately
    const managerState = this.validationManager.getState();
    const isPullType = stringComparison(this.transferType, "Pull");
    const schedulerRequired = isPullType ? this.tabValidation.schedulerValid : true;
    const hasVariableErrors = this.hasAnyVariableErrors();
    const anyMidEdit = managerState.anyMidEdit;

    // All tabs must be valid AND no midEdit states AND no undefined variables
    this.tabValidation.allValid =
      this.tabValidation.settingsValid &&
      this.tabValidation.workflowInformationValid &&
      this.tabValidation.notificationValid &&
      this.tabValidation.stepsValid &&
      schedulerRequired &&
      !hasVariableErrors &&
      !anyMidEdit;

    // Calculate if any tab has unsaved changes
    this.tabValidation.anyDirty =
      this.tabValidation.settingsDirty ||
      this.tabValidation.workflowInformationDirty ||
      this.tabValidation.notificationDirty ||
      this.tabValidation.stepsDirty ||
      this.tabValidation.schedulerDirty;

    this.requestUpdate();
  }

  /**
   * Update validation state for a specific tab
   * Used to programmatically reset validation/dirty state (e.g., after save)
   * @param tabName - Name of the tab to update
   * @param state - Object containing valid and/or dirty values to set
   */
  updateTabValidation(tabName: string, state: { valid?: boolean; dirty?: boolean }) {
    // Map legacy tab names to state manager tab names
    const tabNameMap: Record<string, TabName> = {
      settings: "settings",
      workflowInfo: "workflowInfo",
      notification: "notification",
      steps: "steps",
      scheduler: "scheduler",
    };

    const managerTabName = tabNameMap[tabName];
    if (managerTabName) {
      this.validationManager.updateTabState(managerTabName, state);
    }

    // Also update legacy state for backward compatibility
    switch (tabName) {
      case "settings":
        if (state.valid !== undefined) this.tabValidation.settingsValid = state.valid;
        if (state.dirty !== undefined) this.tabValidation.settingsDirty = state.dirty;
        break;
      case "workflowInfo":
        if (state.valid !== undefined) this.tabValidation.workflowInformationValid = state.valid;
        if (state.dirty !== undefined) this.tabValidation.workflowInformationDirty = state.dirty;
        break;
      case "notification":
        if (state.valid !== undefined) this.tabValidation.notificationValid = state.valid;
        if (state.dirty !== undefined) this.tabValidation.notificationDirty = state.dirty;
        break;
      case "steps":
        if (state.valid !== undefined) this.tabValidation.stepsValid = state.valid;
        if (state.dirty !== undefined) this.tabValidation.stepsDirty = state.dirty;
        break;
      case "scheduler":
        if (state.valid !== undefined) this.tabValidation.schedulerValid = state.valid;
        if (state.dirty !== undefined) this.tabValidation.schedulerDirty = state.dirty;
        break;
    }

    // Recalculate aggregate states
    this.tabValidation.anyDirty =
      this.tabValidation.settingsDirty ||
      this.tabValidation.workflowInformationDirty ||
      this.tabValidation.notificationDirty ||
      this.tabValidation.stepsDirty ||
      this.tabValidation.schedulerDirty;

    const isPullType = stringComparison(this.transferType, "Pull");
    const schedulerRequired = isPullType ? this.tabValidation.schedulerValid : true;

    // Check if any tab has undefined variable errors
    const hasVariableErrors = this.hasAnyVariableErrors();

    this.tabValidation.allValid =
      this.tabValidation.settingsValid &&
      this.tabValidation.workflowInformationValid &&
      this.tabValidation.notificationValid &&
      this.tabValidation.stepsValid &&
      schedulerRequired &&
      !hasVariableErrors;

    this.requestUpdate();
  }

  /**
   * Get reference to child tab components
   */
  getSettingsTab(): any {
    return this.shadowRoot?.querySelector("workflow-template-settings-tab");
  }

  getInfoTab(): any {
    return this.shadowRoot?.querySelector("workflow-template-info-tab");
  }

  getNotificationTab(): any {
    return this.shadowRoot?.querySelector("workflow-template-notification-tab");
  }

  getStepsTab(): any {
    return this.shadowRoot?.querySelector("workflow-template-steps-tab");
  }

  /**
   * [ACME-1422] Resolve the friendly Business Unit display name for the SMB
   * bucket preview shown inside `AddWorkflowStepPopup`. Pulls the currently
   * selected BU id from the Settings tab and looks up the matching name in the
   * Settings tab's already-loaded `businessUnits` catalog. Returns "" for
   * Global templates (no BU bound at author time) or before Settings loads.
   * Evaluated each render of the General view, so tab switches re-resolve.
   */
  get buDisplayName(): string {
    const settingsTab = this.getSettingsTab();
    if (!settingsTab) return "";
    const selected: string = settingsTab.businessUnit || "";
    if (!selected) return "";
    const catalog: Array<{ value: string; name: string }> = settingsTab.businessUnits || [];
    const match = catalog.find((bu) => bu?.value?.toLowerCase() === selected.toLowerCase());
    return match ? match.name : "";
  }

  getSchedulerTab(): any {
    return this.shadowRoot?.querySelector("workflow-template-scheduler-tab");
  }

  /**
   * Handle variable-added event from child components (e.g., WorkflowTemplateInfoTab)
   * Adds the new variable to the Settings tab and syncs variables to all tabs
   */
  onVariableAdded(e: CustomEvent) {
    const variableData = e.detail;

    // Add the variable to the Settings tab
    const settingsTab = this.getSettingsTab();
    if (settingsTab && settingsTab.addVariableFromExternal) {
      const success = settingsTab.addVariableFromExternal(variableData);

      if (success) {
        // Sync variables to Info tab so the new variable appears in dropdowns
        this.syncVariablesToTabs();

        // Re-validate all fields to clear errors for the newly defined variable
        this.revalidateAllVariableFields();
      }
    }
  }

  /**
   * Re-validate all template-string-input fields across all tabs.
   * Called when variables are added/removed to update error states.
   */
  private revalidateAllVariableFields(): void {
    const settingsTab = this.getSettingsTab();
    if (!settingsTab || !settingsTab.getFormData) return;

    const settingsData = settingsTab.getFormData();
    const definedVariables = settingsData?.variables || [];
    const definedVarNames = new Set(
      (definedVariables || []).map((v: any) => (v.name || "").toLowerCase())
    );

    // Find all template-string-input elements
    const templateInputs = this.shadowRoot
      ? this.findAllTemplateStringInputs(this.shadowRoot)
      : [];

    // Re-validate each input
    for (const input of templateInputs) {
      const value = input.value || "";
      const placeholders = this.extractVariablePlaceholders(value);
      const undefinedVars: string[] = [];

      for (const placeholder of placeholders) {
        if (!definedVarNames.has(placeholder.toLowerCase())) {
          if (!undefinedVars.includes(placeholder)) {
            undefinedVars.push(placeholder);
          }
        }
      }

      // Update this input's error state
      if (undefinedVars.length > 0) {
        const varList = undefinedVars.map((v) => `{${v}}`).join(", ");
        const plural = undefinedVars.length > 1 ? "s" : "";
        input.errorText = `Variable${plural} ${varList} ${undefinedVars.length > 1 ? "have" : "has"} not been defined`;
        input.forceShowWarning = true;
      } else {
        // Clear error if it was a variable validation error
        if (input.errorText?.includes("not been defined")) {
          input.errorText = "";
          input.forceShowWarning = false;
        }
      }
    }

    // Update variable error states for all tabs
    const tabNames = ["Template Settings", "Workflow Information", "Notifications", "Workflow Steps", "Scheduler"];
    for (const tabName of tabNames) {
      this.updateVariableErrorsForTab(tabName, definedVarNames);
    }

    this.updateVariablesInUse();
  }

  /**
   * Sync variables from Settings tab to all other tabs that need them
   * This ensures variable dropdowns are updated when variables are added/removed
   */
  syncVariablesToTabs() {
    const settingsTab = this.getSettingsTab();
    const infoTab = this.getInfoTab();
    const notificationTab = this.getNotificationTab();
    const stepsTab = this.getStepsTab();
    const schedulerTab = this.getSchedulerTab();

    if (!settingsTab) return;

    const variables = settingsTab.getVariables ? settingsTab.getVariables() : [];

    // Update Info tab
    if (infoTab) {
      infoTab.variables = variables;
    }

    // Update Notification tab (if it has variable support)
    if (notificationTab && "variables" in notificationTab) {
      notificationTab.variables = variables;
    }

    // Update Steps tab (if it has variable support)
    if (stepsTab && "variables" in stepsTab) {
      stepsTab.variables = variables;
    }

    // Update Scheduler tab (if it has variable support)
    if (schedulerTab && "variables" in schedulerTab) {
      schedulerTab.variables = variables;
    }
  }

  /**
   * Extract all {variableName} template placeholders from a string (names only).
   */
  private extractVariablePlaceholders(text: string | null | undefined): string[] {
    return extractTemplateVariableNames(text);
  }

  /**
   * Strings that may contain user-authored `{variable}` / legacy `[variable]` syntax.
   * Scoped to the same areas as template-string-input (and step payload equivalents), so we do not
   * scan the whole save payload — e.g. variables[].regexRule contains `[A-Za-z]` and must not block save.
   */
  private pushStepTemplateStringsForBracketScan(step: unknown, strings: string[]): void {
    for (const value of collectStepTemplateFieldStrings(step)) {
      strings.push(value);
    }
  }

  private collectStringsForDeprecatedBracketScan(templateRequest: Record<string, unknown>): string[] {
    const strings: string[] = [];
    const push = (v: unknown) => {
      if (typeof v === "string" && v) strings.push(v);
    };

    push(templateRequest.workflowName);
    push(templateRequest.sourceLocationPath);

    const tfp = templateRequest.triggerFilePattern;
    if (Array.isArray(tfp)) {
      for (const x of tfp) push(x);
    }
    const dfp = templateRequest.dataFilePattern;
    if (Array.isArray(dfp)) {
      for (const x of dfp) push(x);
    }

    const notif = templateRequest.notification;
    if (Array.isArray(notif)) {
      for (const n of notif as Record<string, unknown>[]) {
        push(n?.to);
        push(n?.subject);
        push(n?.template);
      }
    }

    const steps = templateRequest.workflowSteps;
    if (Array.isArray(steps)) {
      for (const step of steps) {
        this.pushStepTemplateStringsForBracketScan(step, strings);
      }
    }

    const sched = templateRequest.scheduler;
    if (sched && typeof sched === "object" && Object.keys(sched as object).length > 0) {
      const sch = sched as Record<string, unknown>;
      push(sch.schedulerName ?? sch.SchedulerName);
      push(sch.description ?? sch.Description);
    }

    if (this.shadowRoot) {
      for (const inp of this.findAllTemplateStringInputs(this.shadowRoot)) {
        push(inp.value);
      }
    }

    return strings;
  }

  /** Deprecated legacy [var] names in template-eligible fields only (not regex rules or unrelated payload). */
  private findDeprecatedBracketNamesInPayload(obj: unknown): string[] {
    const templateRequest = obj as Record<string, unknown>;
    const strings = this.collectStringsForDeprecatedBracketScan(templateRequest);
    const names = new Set<string>();
    for (const s of strings) {
      findDeprecatedLegacyBracketTemplateNames(s).forEach((n) => names.add(n));
    }
    return Array.from(names);
  }

  /**
   * ACME-1699: every user-added Settings grid variable must appear in a saved template field.
   * The two system-forced names (region and business unit) are skipped — the UI inserts those
   * because a field is disabled, and they are not rows the user added.
   */
  private findUnusedDefinedVariablesInPayload(
    definedVariables: { name?: string }[],
    templateRequest: Record<string, unknown>
  ): string[] {
    const strings = this.collectStringsForDeprecatedBracketScan(templateRequest);
    const usedLower = new Set<string>();
    for (const s of strings) {
      for (const name of extractTemplateVariableNames(s)) {
        usedLower.add(name.toLowerCase());
      }
    }

    const unused: string[] = [];
    for (const variable of definedVariables || []) {
      const name = (variable?.name || "").trim();
      if (!name) continue;
      if (isBuiltinRegionVariableName(name) || isBuiltinBusUnitVariableName(name)) continue;
      if (!usedLower.has(name.toLowerCase()) && !unused.some((u) => u.toLowerCase() === name.toLowerCase())) {
        unused.push(name);
      }
    }
    return unused;
  }

  private refreshLegacyBracketWarningFromDom(): void {
    if (!this.shadowRoot) {
      this.showLegacyBracketWarning = false;
      return;
    }
    const inputs = this.findAllTemplateStringInputs(this.shadowRoot);
    this.showLegacyBracketWarning = inputs.some((inp) =>
      hasDeprecatedLegacyBracketTemplateSyntax(inp.value || "")
    );
  }

  /**
   * Collect all variable names currently in use across all template fields.
   * Prefers live tab form data (edit-in-progress) over loaded templateData so removing
   * a {variable} from an input updates delete-button / in-use state immediately.
   * Also scans current template-string-input values in the DOM.
   */
  private collectVariablesInUse(): string[] {
    const usedVariables = new Set<string>();

    const infoData = this.getInfoTab()?.getFormData?.();
    const notificationData = this.getNotificationTab()?.getFormData?.();
    const stepsData = this.getStepsTab()?.getFormData?.();
    const schedulerData = this.getSchedulerTab()?.getFormData?.();

    // When a tab is dirty (or there is no loaded template), live getFormData is authoritative.
    // Otherwise prefer templateData so we don't miss usages before child tabs finish syncing.
    const useLiveInfo =
      !!infoData && (this.tabValidation.workflowInformationDirty || !this.templateData);
    const useLiveNotifications =
      !!notificationData && (this.tabValidation.notificationDirty || !this.templateData);
    const useLiveSteps = !!stepsData && (this.tabValidation.stepsDirty || !this.templateData);
    const useLiveScheduler =
      !!schedulerData && (this.tabValidation.schedulerDirty || !this.templateData);

    if (useLiveInfo) {
      this.scanFieldForVariables(infoData.workflowName, usedVariables);
      this.scanFieldForVariables(infoData.sourceLocationPath, usedVariables);
      this.scanArrayForVariables(infoData.triggerFilePatterns, usedVariables);
      this.scanArrayForVariables(infoData.dataFilePatterns, usedVariables);
    } else if (this.templateData) {
      this.scanFieldForVariables(this.templateData.workflowName, usedVariables);
      this.scanFieldForVariables(this.templateData.sourceLocationPath, usedVariables);
      this.scanArrayForVariables(this.templateData.triggerFilePattern, usedVariables);
      this.scanArrayForVariables(this.templateData.dataFilePattern, usedVariables);
    }

    const notifications = useLiveNotifications
      ? notificationData.notifications
      : this.templateData?.notification;
    if (Array.isArray(notifications)) {
      for (const notif of notifications) {
        this.scanFieldForVariables(notif?.to, usedVariables);
        this.scanFieldForVariables(notif?.subject, usedVariables);
        this.scanFieldForVariables(notif?.template, usedVariables);
      }
    }

    const steps = useLiveSteps ? stepsData.steps : this.templateData?.workflowSteps;
    if (Array.isArray(steps)) {
      for (const step of steps) {
        for (const value of collectStepTemplateFieldStrings(step)) {
          this.scanFieldForVariables(value, usedVariables);
        }
      }
    }

    const schedData = useLiveScheduler
      ? schedulerData.scheduler
      : this.templateData?.scheduler;
    if (schedData && typeof schedData === "object" && Object.keys(schedData).length > 0) {
      this.scanFieldForVariables(schedData.schedulerName ?? schedData.SchedulerName, usedVariables);
      this.scanFieldForVariables(schedData.description ?? schedData.Description, usedVariables);
    }

    // Also scan DOM for any unsaved changes in template-string-input fields
    if (this.shadowRoot) {
      const templateInputs = this.findAllTemplateStringInputs(this.shadowRoot);
      for (const input of templateInputs) {
        const value = input.value || "";
        const placeholders = this.extractVariablePlaceholders(value);
        placeholders.forEach(p => usedVariables.add(p));
      }
    }

    this.refreshLegacyBracketWarningFromDom();
    return Array.from(usedVariables);
  }

  /**
   * Extract variable placeholders from a single field value
   */
  private scanFieldForVariables(value: any, usedVariables: Set<string>): void {
    if (typeof value === "string") {
      const placeholders = this.extractVariablePlaceholders(value);
      placeholders.forEach(p => usedVariables.add(p));
    }
  }

  /**
   * Extract variable placeholders from an array of strings
   */
  private scanArrayForVariables(arr: any, usedVariables: Set<string>): void {
    if (!Array.isArray(arr)) return;
    for (const item of arr) {
      if (typeof item === "string") {
        const placeholders = this.extractVariablePlaceholders(item);
        placeholders.forEach(p => usedVariables.add(p));
      }
    }
  }

  /**
   * Update the variablesInUse state by scanning all template fields
   * Called when template inputs change or data is loaded
   */
  private updateVariablesInUse(): void {
    this.variablesInUse = this.collectVariablesInUse();
  }

  /**
   * Determine which tab a TemplateStringInput element belongs to
   * by traversing up the DOM tree to find the parent tab component
   */
  private getTabNameForElement(element: Element): string {
    let current: Element | null = element;

    while (current) {
      const tagName = current.tagName.toLowerCase();

      // Check for known tab component tags
      if (tagName === "workflow-template-info-tab") {
        return "Workflow Information";
      }
      if (tagName === "workflow-template-notification-tab" ||
        tagName === "workflow-template-notification-view") {
        return "Notifications";
      }
      if (tagName === "workflow-template-steps-tab" ||
        tagName === "workflow-template-steps-view") {
        return "Workflow Steps";
      }
      if (tagName === "workflow-template-scheduler-tab" ||
        tagName === "workflow-template-scheduler-view") {
        return "Scheduler";
      }
      if (tagName === "workflow-template-settings-tab") {
        return "Template Settings";
      }
      if (tagName === "workflow-template-file-pattern-view") {
        return "Workflow Information";
      }

      // Move up to parent, handling shadow DOM boundaries
      if (current.parentElement) {
        current = current.parentElement;
      } else if (current.getRootNode() instanceof ShadowRoot) {
        current = (current.getRootNode() as ShadowRoot).host;
      } else {
        current = null;
      }
    }

    return "Unknown Tab";
  }

  /**
   * Recursively find all template-string-input elements in a component tree
   * Traverses into shadow DOM of child components
   */
  private findAllTemplateStringInputs(root: Element | ShadowRoot): TemplateStringInput[] {
    const inputs: TemplateStringInput[] = [];

    // Query direct template-string-input elements
    const directInputs = root.querySelectorAll("template-string-input");
    directInputs.forEach((input) => {
      inputs.push(input as TemplateStringInput);
    });

    // Find all custom elements that might have shadow DOM
    const allElements = root.querySelectorAll("*");
    allElements.forEach((element) => {
      if (element.shadowRoot) {
        // Recursively search in shadow DOM
        const shadowInputs = this.findAllTemplateStringInputs(element.shadowRoot);
        inputs.push(...shadowInputs);
      }
    });

    return inputs;
  }

  /**
   * Validate that all variable placeholders used in TemplateStringInput fields are defined.
   * Dynamically discovers all template-string-input elements and checks their values.
   * Returns an object with validation result and details about undefined variables.
   * Note: This is a simple save-time validation - no side effects on inputs.
   */
  private validateVariableUsage(
    definedVariables: { name: string }[]
  ): { valid: boolean; errors: { tab: string; field: string; undefinedVars: string[] }[] } {
    const errors: { tab: string; field: string; undefinedVars: string[] }[] = [];

    // Get the list of defined variable names (case-insensitive comparison)
    // Note: Variables should always have camelCase 'name' property from getFormData()
    const definedVarNames = new Set(
      (definedVariables || []).map((v: any) => (v.name || "").toLowerCase())
    );

    // Find all template-string-input elements in this component's shadow DOM tree
    const templateInputs = this.shadowRoot
      ? this.findAllTemplateStringInputs(this.shadowRoot)
      : [];

    // Check each template-string-input for undefined variables
    for (const input of templateInputs) {
      const value = input.value || "";
      const placeholders = this.extractVariablePlaceholders(value);
      const undefinedVars: string[] = [];

      for (const placeholder of placeholders) {
        if (isBuiltinRegionVariableName(placeholder)) {
          continue;
        }
        if (!definedVarNames.has(placeholder.toLowerCase())) {
          if (!undefinedVars.includes(placeholder)) {
            undefinedVars.push(placeholder);
          }
        }
      }

      if (undefinedVars.length > 0) {
        // Get the field label from the input's label property
        const fieldLabel = input.label || "Unknown Field";
        const tabName = this.getTabNameForElement(input);

        errors.push({
          tab: tabName,
          field: fieldLabel,
          undefinedVars
        });
      }
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Handle Save button click
   */
  async onSaveClick() {
    if (!this.canEditWorkflowTemplate) {
      showToastMessage("You do not have permission to save changes to this template.", "danger");
      return;
    }

    if (!this.tabValidation.allValid) {
      showToastMessage("Please complete all required fields in all tabs before saving.", "danger");
      return;
    }

    this.saveInProgress = true;

    try {
      // 1. Collect data from child components
      const settingsTab = this.getSettingsTab();
      const infoTab = this.getInfoTab();
      const notificationTab = this.getNotificationTab();
      const stepsTab = this.getStepsTab();
      const schedulerTab = this.getSchedulerTab();

      if (!settingsTab || !infoTab) {
        showToastMessage("Unable to collect form data. Please try again.", "danger");
        return;
      }

      const settingsData = settingsTab.getFormData();
      const infoData = infoTab.getFormData();
      const notificationData = notificationTab?.getFormData() || { notifications: [] };
      const stepsData = stepsTab?.getFormData() || { steps: [], deleteFiles: true };
      const schedulerData = schedulerTab?.getFormData() || { scheduler: {} };

      const variablesForApiSave = ensureVariablesIncludeBuiltinRegionForApi(
        settingsData.variables || [],
        stepsData.steps
      );

      // 1.5 Validate that all variable placeholders are defined
      // Uses dynamic discovery of all template-string-input elements
      const variableValidation = this.validateVariableUsage(settingsData.variables);

      if (!variableValidation.valid) {
        showToastMessage(
          `Cannot save: Some fields use undefined variables. Please define these variables in Template Settings or remove them from the fields.\n\n${variableValidation.errors.map(e => `• ${e.tab}: ${e.undefinedVars.map((v) => `{${v}}`).join(", ")}`).join("\n")}`,
          "danger"
        );
        this.saveInProgress = false;
        return;
      }

      // Get current user info - prefer userId (actual user ID) over userName (display name)
      const currentUser = decryptValue(Cookies.get("userId") || "") || decryptValue(Cookies.get("userName") || "") || "Unknown";

      // Build the template request with camelCase property names (C# API JSON serialization)
      const templateRequest = {
        templateName: settingsData.templateName,
        description: settingsData.templateDescription,
        // Category: "global" or "businessunit" (lowercase for API)
        category: settingsData.visibilityLevel === "Global" ? "global" : "businessunit",
        businessUnitId: settingsData.businessUnitId || null,
        // BusinessUnitParticipant is used by the API to construct S3 paths when generating workflows
        businessUnitParticipant: settingsData.businessUnitId || "",
        status: settingsData.status || "inactive",
        createdBy: currentUser,
        updatedBy: currentUser,
        // Workflow Info fields - camelCase for API
        workflowName: infoData.workflowName,
        // Required when generating workflows from template (AWS processing); not shown in UI
        workflowType: DEFAULT_WORKFLOW_TYPE_ODI,
        transferType: infoData.transferType,
        sourceLocationPath: infoData.sourceLocationPath,
        triggerFilePattern: infoData.triggerFilePatterns,
        dataFilePattern: infoData.dataFilePatterns,
        // Variables (includes synthetic BUILTIN_REGION_VARIABLE when S3 Remote steps need it)
        variables: variablesForApiSave,
        // Notifications (optional) - API uses "notification" (singular)
        notification: notificationData.notifications,
        // Steps (optional) - API uses "workflowSteps"
        workflowSteps: stepsData.steps,
        deleteFiles: stepsData.deleteFiles,
        // Scheduler: full object for Pull; empty {} for Push (backend requires key present)
        scheduler: schedulerData.scheduler,
      };

      const deprecatedNames = this.findDeprecatedBracketNamesInPayload(templateRequest);
      if (deprecatedNames.length > 0) {
        const sample = deprecatedNames.slice(0, 8).map((n) => `[${n}]`).join(", ");
        showToastMessage(
          `Cannot save: This template still uses deprecated square-bracket placeholders (${sample}${deprecatedNames.length > 8 ? ", …" : ""}). Replace them with {variableName} syntax. Predefined processing tokens such as [YYYY] or [TRACE_ID] may stay in square brackets—type those manually; they are not chosen from the variable picker.`,
          "danger"
        );
        this.saveInProgress = false;
        return;
      }

      // ACME-1699: every user-defined variable must appear in saved template fields
      const unusedDefinedVariables = this.findUnusedDefinedVariablesInPayload(
        settingsData.variables || [],
        templateRequest
      );
      if (unusedDefinedVariables.length > 0) {
        const unusedList = unusedDefinedVariables.map((v) => `{${v}}`).join(", ");
        showToastMessage(
          `Cannot save: defined variable(s) not used: ${unusedList}. Any defined variable must be used or removed from the template.`,
          "danger"
        );
        this.saveInProgress = false;
        return;
      }

      const credentialLiteralIssues = findWorkflowTemplateStepCredentialLiterals(templateRequest.workflowSteps);
      if (credentialLiteralIssues.length > 0) {
        const lines = credentialLiteralIssues.map(
          (i) => `• Step ${i.stepNumber} (${i.stepName}): ${i.fieldLabel} — "${i.preview}"`
        );
        showToastMessage(
          `Cannot save: credential fields must use only variable placeholders (e.g. {myVar}), not literal values.\n\n${lines.join("\n")}`,
          "danger"
        );
        this.saveInProgress = false;
        return;
      }

      let response;

      // 3. Save to backend (ADD or EDIT mode)
      if (this.mode === "ADD") {
        response = await postRequest(Apis.ADD_WORKFLOW_TEMPLATE, templateRequest);
      } else {
        // EDIT mode - templateName is already in templateRequest (camelCase)
        response = await putRequest(
          `${Apis.UPDATE_WORKFLOW_TEMPLATE}/${fixedEncodeURIComponent(this.templateName)}`,
          this.templateETag, // ETag for optimistic locking
          templateRequest
        );
      }

      // 4. Handle response - Success: messageStatus === null (Workflow pattern)
      if (response?.messageStatus === null) {
        // Success! Reset dirty states and navigate
        try {
          this.updateTabValidation("settings", { valid: true, dirty: false });
          this.updateTabValidation("workflowInfo", { valid: true, dirty: false });
          this.updateTabValidation("notification", { valid: true, dirty: false });
          this.updateTabValidation("steps", { valid: true, dirty: false });
          this.updateTabValidation("scheduler", { valid: true, dirty: false });
        } catch {
          // Ignore errors during state cleanup - we're navigating away anyway
        }

        showToastMessage("Template saved successfully!", "success");

        // Notify main list about the template change
        if (this.mode === "ADD") {
          // New template - dispatch template-created to trigger full refresh
          // (new template needs to be fetched from API and sorted into correct position)
          document.dispatchEvent(
            new CustomEvent("template-created", {
              detail: {
                templateName: settingsData.templateName,
              },
            })
          );
        } else {
          // Existing template - dispatch template-updated to update in-place (avoids full re-fetch)
          // Capitalize status to match API format ("Active"/"Inactive")
          const statusCapitalized = settingsData.status
            ? settingsData.status.charAt(0).toUpperCase() + settingsData.status.slice(1)
            : "Inactive";
          document.dispatchEvent(
            new CustomEvent("template-updated", {
              detail: {
                templateName: settingsData.templateName,
                description: settingsData.templateDescription,
                category: settingsData.visibilityLevel === "Global" ? "global" : "businessunit",
                status: statusCapitalized,
                updatedBy: response?.updatedBy || currentUser,
                updateDateTime: response?.updateDateTime || new Date().toISOString(),
              },
            })
          );
        }

        // Navigate back to main list
        AppRouters.go("/workflow-templates");
        return; // Exit early to avoid catch block
      } else if (response?.details?.length > 0) {
        let errMsg = getErrorMessage(response.details).trim();
        if (/credential/i.test(errMsg) && !/Step\s+\d+/i.test(errMsg)) {
          errMsg +=
            "\n\nCheck Workflow Steps: open each step and ensure Connection String, passwords, host, Role ARN, etc. use only placeholders like {variableName} defined under Template Settings.";
        }
        showToastMessage(errMsg, "danger");
      } else {
        showToastMessage("Something went wrong. Please try again.", "danger");
      }
    } catch {
      showToastMessage("Failed to save template. Please try again.", "danger");
    } finally {
      this.saveInProgress = false;
    }
  }

  /**
   * Handle Cancel button click
   * ACME-1050: no change → leave silently; dirty → openJhftDialog warning.
   */
  async onCancelClick() {
    const shouldConfirmUnsaved = this.tabValidation.anyDirty && this.canEditWorkflowTemplate;
    if (shouldConfirmUnsaved) {
      const result = await openJhftDialog({
        variant: "warning",
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to leave without saving?",
        cancelLabel: "Stay",
        confirmLabel: "Leave Without Saving",
      });
      if (result !== "confirm") {
        return;
      }
    }
    AppRouters.go("/workflow-templates");
  }

  /**
   * Render tab icon based on validation state (3-state system)
   *
   * Icon Logic (Three States):
   * 1. Invalid (orange warning): Tab has problems that must be fixed before saving
   *    - Required fields missing
   *    - Has undefined variable errors
   *    - Has mid-edit state (row/field being edited but not committed)
   * 2. Valid + Dirty (save icon): Tab is complete but has unsaved changes
   * 3. Valid + Clean (filled green checkmark): Tab is saved and valid
   *
   * Special case: Scheduler tab when Transfer Type is "Push" - always shows green checkmark
   * because scheduler is not required for Push workflows.
   *
   * No icons shown in VIEW mode.
   *
   * @param valid - Whether all required fields are filled
   * @param tabName - Name of the tab
   */
  renderTabIcon(valid: boolean, tabName: string) {
    // Don't show validation icons in VIEW mode
    if (this.mode === "VIEW") {
      return "";
    }

    // Special case: Scheduler tab when Transfer Type is "Push"
    // Scheduler is not required for Push workflows, so always show green checkmark
    if (tabName === "scheduler" && !stringComparison(this.transferType, "Pull")) {
      return html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`;
    }

    // Check if this tab has variable validation errors (undefined variables used)
    const tabKey = tabName as keyof typeof this.variableErrors;
    const hasVariableErrors = this.variableErrors[tabKey] || false;

    // Check if this tab has mid-edit state (row/field being edited but not committed)
    const managerState = this.validationManager?.getState();
    let isMidEdit = false;
    if (managerState) {
      const tabState = managerState.tabs[tabName as keyof typeof managerState.tabs];
      if (tabState) {
        isMidEdit = tabState.midEdit;
      }
    }

    // STATE 1: Invalid - show warning icon (orange/amber)
    // Tab is invalid if: required fields missing, has undefined variable errors, or mid-edit
    if (!valid || hasVariableErrors || isMidEdit) {
      return html`<jha-icon-warning-filled icon></jha-icon-warning-filled>`;
    }

    // Check if this specific tab has dirty/unsaved data
    let isDirty = false;
    switch (tabName) {
      case "settings":
        isDirty = this.tabValidation.settingsDirty;
        break;
      case "workflowInfo":
        isDirty = this.tabValidation.workflowInformationDirty;
        break;
      case "notification":
        isDirty = this.tabValidation.notificationDirty;
        break;
      case "steps":
        isDirty = this.tabValidation.stepsDirty;
        break;
      case "scheduler":
        isDirty = this.tabValidation.schedulerDirty;
        break;
    }

    // STATE 2: Valid + Dirty - show save/floppy disk icon (ready to save)
    if (isDirty) {
      return html`<jha-icon-save icon></jha-icon-save>`;
    }

    // STATE 3: Valid + Clean - show filled green checkmark (saved and valid)
    return html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`;
  }

  render() {
    return html`
      ${this.apiLoading ? html`<circle-loader></circle-loader>` : ""}
      <div id="template-container">
        <h1>
          <span>${this.pageTitle}</span>
        </h1>
        ${this.mode !== "VIEW" && this.showLegacyBracketWarning
        ? html`
        <div class="legacy-bracket-banner" data-auto-id="legacyBracketSyntaxWarning" role="status">
          This template still uses deprecated <strong>[variableName]</strong> placeholders for template variables.
          Replace them with <strong>{variableName}</strong>. Predefined processing tokens (for example
          <strong>[YYYY]</strong> or <strong>[TRACE_ID]</strong>) stay in square brackets—type them manually;
          they are not selected from the variable menu.
        </div>
        `
        : nothing}
        <div id="tabsWrapper">
          <jha-tab bordered>
            <!-- Template Settings Tab -->
            <jha-tab-item data-auto-id="settingsTab" ?active=${this.activeTab === "settings"} @click=${() =>
        this.onTabClick("settings")}>
              Template Settings ${this.renderTabIcon(this.tabValidation.settingsValid, "settings")}
            </jha-tab-item>
      
            <!-- Workflow Information Tab -->
            <jha-tab-item data-auto-id="workflowInfoTab" ?active=${this.activeTab === "workflowInfo"} @click=${() =>
        this.onTabClick("workflowInfo")}>
              Workflow Information ${this.renderTabIcon(this.tabValidation.workflowInformationValid, "workflowInfo")}
            </jha-tab-item>
      
            <!-- Notification Tab -->
            <jha-tab-item data-auto-id="notificationTab" ?active=${this.activeTab === "notification"} @click=${() =>
        this.onTabClick("notification")}>
              Notifications ${this.renderTabIcon(this.tabValidation.notificationValid, "notification")}
            </jha-tab-item>
      
            <!-- Workflow Steps Tab -->
            <jha-tab-item data-auto-id="stepsTab" ?active=${this.activeTab === "steps"} @click=${() =>
        this.onTabClick("steps")}>
              Workflow Steps ${this.renderTabIcon(this.tabValidation.stepsValid, "steps")}
            </jha-tab-item>
      
            <!-- Scheduler Tab -->
            <jha-tab-item data-auto-id="schedulerTab" ?active=${this.activeTab === "scheduler"} @click=${() =>
        this.onTabClick("scheduler")}>
              Scheduler ${this.renderTabIcon(this.tabValidation.schedulerValid, "scheduler")}
            </jha-tab-item>
          </jha-tab>
      
          <!-- Tab Content - All tabs are always rendered but hidden when inactive -->
          <!-- This preserves component state when switching between tabs -->
          <!-- templateData is fetched once by parent and passed to all tabs to avoid duplicate API calls -->
          <div id="tabContent">
            <div class="tab-panel" ?active=${this.activeTab === "settings"}>
              <workflow-template-settings-tab data-auto-id="settingsTabPanel" .mode=${this.mode} .workflowTemplateId=${this.workflowTemplateId}
                .isReadOnly=${this.mode === "VIEW"} .templateData=${this.templateData} .variablesInUse=${this.variablesInUse}
                @validation-change=${this.onTabValidationChange}>
              </workflow-template-settings-tab>
            </div>
      
            <div class="tab-panel" ?active=${this.activeTab === "workflowInfo"}>
              <workflow-template-info-tab data-auto-id="workflowInfoTabPanel" .mode=${this.mode} .workflowTemplateId=${this.workflowTemplateId}
                .isReadOnly=${this.mode === "VIEW"} .templateData=${this.templateData}
                @validation-change=${this.onTabValidationChange} @variable-added=${this.onVariableAdded}>
              </workflow-template-info-tab>
            </div>
      
            <div class="tab-panel" ?active=${this.activeTab === "notification"}>
              <workflow-template-notification-tab data-auto-id="notificationTabPanel" .mode=${this.mode} .workflowTemplateId=${this.workflowTemplateId}
                .isReadOnly=${this.mode === "VIEW"} .templateData=${this.templateData}
                @validation-change=${this.onTabValidationChange} @variable-added=${this.onVariableAdded}>
              </workflow-template-notification-tab>
            </div>
      
            <div class="tab-panel" ?active=${this.activeTab === "steps"}>
              <workflow-template-steps-tab data-auto-id="stepsTabPanel" .mode=${this.mode} .workflowTemplateId=${this.workflowTemplateId}
                .isReadOnly=${this.mode === "VIEW"} .transferType=${this.transferType} .templateData=${this.templateData}
                .buDisplayName=${this.buDisplayName}
                @validation-change=${this.onTabValidationChange} @variable-added=${this.onVariableAdded}>
              </workflow-template-steps-tab>
            </div>
      
            <div class="tab-panel" ?active=${this.activeTab === "scheduler"}>
              <workflow-template-scheduler-tab data-auto-id="schedulerTabPanel" .mode=${this.mode} .workflowTemplateId=${this.workflowTemplateId}
                .isReadOnly=${this.mode === "VIEW"} .transferType=${this.transferType} .templateData=${this.templateData}
                @validation-change=${this.onTabValidationChange} @variable-added=${this.onVariableAdded}>
              </workflow-template-scheduler-tab>
            </div>
          </div>
      
          <!-- Action Buttons -->
          <div id="actionBtns">
            ${this.mode !== "VIEW"
        ? html`
            <jha-tooltip top text="${this.tabValidation.anyDirty ? " Click to save and exit." : "No changes to save."}">
              <jha-button data-auto-id="saveTemplateBtn" type="button" sync ?disabled=${!this.tabValidation.allValid ||
          !this.tabValidation.anyDirty || this.saveInProgress || this.apiLoading} ?isLoading=${this.saveInProgress}
                @click=${this.onSaveClick}>
                Save Template
              </jha-button>
            </jha-tooltip>
            `
        : ""}
            <jha-tooltip top text=${this.canEditWorkflowTemplate ? "Click to cancel and exit without saving."
        : "Click to close."}>
              <jha-button data-auto-id="cancelTemplateBtn" type="button" outline sync @click=${this.onCancelClick}>
                ${this.canEditWorkflowTemplate ? "Cancel" : "Close"}
              </jha-button>
            </jha-tooltip>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "workflow-template-general-view": WorkflowTemplateGeneralView;
  }
}
